import { formatCurrency } from '../../PolicyAdmin/utils';
import type { DimensionSlice } from '../types';
import { colorForIndex } from './chartColors';

const SIZE = 180;
const RADIUS = 70;
const STROKE = 26;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export interface DonutChartProps {
  slices: DimensionSlice[];
  /** Large text in the middle of the ring. */
  centerValue: string;
  centerLabel: string;
  /** What the arc lengths represent, for the accessible description. */
  measureLabel?: string;
}

/**
 * Ring chart drawn with dash offsets on a single circle per slice — cheaper and
 * far less code than arc paths, and it animates cleanly if we ever want it to.
 */
export const DonutChart: React.FC<DonutChartProps> = ({
  slices,
  centerValue,
  centerLabel,
  measureLabel = 'premium',
}) => {
  const total = slices.reduce((sum, slice) => sum + Math.abs(slice.premium), 0);

  if (!total) {
    return (
      <p className="py-10 text-center text-sm" style={{ color: 'var(--color-text-secondary)' }}>
        Nothing to break down for this period.
      </p>
    );
  }

  let offset = 0;
  const arcs = slices.map((slice, index) => {
    const fraction = Math.abs(slice.premium) / total;
    const arc = {
      slice,
      color: colorForIndex(index, slice.key),
      dash: fraction * CIRCUMFERENCE,
      offset,
    };
    offset += fraction * CIRCUMFERENCE;
    return arc;
  });

  return (
    <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-center">
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="h-40 w-40 flex-shrink-0"
        role="img"
        aria-label={`Breakdown by ${measureLabel}: ${slices
          .map(s => `${s.label} ${s.sharePct.toFixed(0)} percent`)
          .join(', ')}`}
      >
        <g transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}>
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke="var(--color-hover-background)"
            strokeWidth={STROKE}
          />
          {arcs.map(arc => (
            <circle
              key={arc.slice.key}
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={RADIUS}
              fill="none"
              stroke={arc.color}
              strokeWidth={STROKE}
              strokeDasharray={`${arc.dash} ${CIRCUMFERENCE - arc.dash}`}
              strokeDashoffset={-arc.offset}
            />
          ))}
        </g>
        <text
          x={SIZE / 2}
          y={SIZE / 2 - 2}
          textAnchor="middle"
          fontSize={22}
          fontWeight={700}
          fill="var(--color-text-primary)"
        >
          {centerValue}
        </text>
        <text
          x={SIZE / 2}
          y={SIZE / 2 + 18}
          textAnchor="middle"
          fontSize={11}
          fill="var(--color-text-secondary)"
        >
          {centerLabel}
        </text>
      </svg>

      <ul className="min-w-0 flex-1 space-y-2">
        {arcs.map(arc => (
          <li key={arc.slice.key} className="flex items-center gap-2.5 text-sm">
            <span
              className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
              style={{ backgroundColor: arc.color }}
              aria-hidden="true"
            />
            <span
              className="min-w-0 flex-1 truncate"
              style={{ color: 'var(--color-text-primary)' }}
            >
              {arc.slice.label}
            </span>
            <span
              className="flex-shrink-0 text-xs tabular-nums"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {arc.slice.count} · {formatCurrency(arc.slice.premium)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};
